import { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { FilterTab } from '../../store/salesOrderWorkspaceStore';

interface FilterDef {
  key: FilterTab;
  label: string;
}

// Order: Drafts (default) → Submitted → separator → Archived as deliberate
// destination at the bottom.
const FILTERS: FilterDef[] = [
  { key: 'drafts', label: 'Drafts' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'archived', label: 'Archived' },
];

interface Props {
  statusFilter: FilterTab;
  onStatusChange: (next: FilterTab) => void;
  quickFilter: string;
  onQuickFilterChange: (next: string) => void;
}

// Single-row command bar mirroring QuotationListFilters: search input on the
// left, outline dropdown filter on the right. No Live pill in the bar (the
// quotation team removed theirs because it crowded the search input).
export const SalesOrderListFilters = forwardRef<HTMLInputElement, Props>(
  function SalesOrderListFilters(
    { statusFilter, onStatusChange, quickFilter, onQuickFilterChange },
    ref,
  ) {
    const active = FILTERS.find((f) => f.key === statusFilter) ?? FILTERS[0];

    // Local-state-with-debounce: instant typing, 250ms-debounced upstream
    // push so the master list doesn't refetch on every keystroke.
    const [localValue, setLocalValue] = useState(quickFilter);

    useEffect(() => {
      setLocalValue((prev) => (prev === quickFilter ? prev : quickFilter));
    }, [quickFilter]);

    const onChangeRef = useRef(onQuickFilterChange);
    useEffect(() => {
      onChangeRef.current = onQuickFilterChange;
    }, [onQuickFilterChange]);

    useEffect(() => {
      if (localValue === quickFilter) return;
      const timer = setTimeout(() => {
        onChangeRef.current(localValue);
      }, 250);
      return () => clearTimeout(timer);
    }, [localValue, quickFilter]);

    return (
      <div className="flex items-center gap-1.5 border-b bg-background px-3 py-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={ref}
            placeholder="Search…"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="h-8 pl-8 text-sm"
            aria-label="Search sales orders"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1 px-2 text-xs font-medium"
              title={`Filter: ${active.label}`}
              aria-label={`Filter: ${active.label}`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Filter</span>
              <span className="font-normal normal-case">{active.label}</span>
            </DropdownMenuLabel>
            {FILTERS.map((f) => {
              const selected = f.key === statusFilter;
              const showSeparatorBefore = f.key === 'archived';
              return (
                <Fragment key={f.key}>
                  {showSeparatorBefore && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onSelect={() => onStatusChange(f.key)}
                    className={cn('gap-2 text-sm', selected && 'bg-accent font-medium')}
                  >
                    <span className="flex-1">{f.label}</span>
                  </DropdownMenuItem>
                </Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  },
);
